import { useEffect, useState } from 'react';
import adminApi from '@/lib/adminApi';

const reviewOptions = [
  { value: 'pending_review', label: 'Pending review' },
  { value: 'published', label: 'Publish' },
  { value: 'admin_rejected', label: 'Reject / needs changes' },
  { value: 'closed', label: 'Close' },
];

const statusLabel = {
  artist_accepted: 'Artist accepted',
  artist_declined: 'Declined',
  in_review: 'Poster reviewing',
  poster_accepted: 'Selected',
  poster_rejected: 'Not selected',
};

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [responses, setResponses] = useState([]);
  const [counts, setCounts] = useState({});
  const [savingId, setSavingId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const response = await adminApi.get(`/admin/projects${showDeleted ? '?include_deleted=true' : ''}`);
      setProjects(response.data.projects || []);
      setResponses(response.data.responses || []);
      setCounts(response.data.counts || {});
      setSelectedProjectIds([]);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to load projects.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeleted]);

  const updateProject = async (project, updates = {}) => {
    setSavingId(project.id);
    setError('');
    try {
      await adminApi.put(`/admin/projects/${project.id}`, {
        review_status: updates.review_status ?? project.review_status ?? 'pending_review',
        is_active: updates.is_active ?? Boolean(project.is_active),
        is_featured: updates.is_featured ?? Boolean(project.is_featured),
        admin_note: updates.admin_note ?? project.admin_note ?? '',
      });
      await loadProjects();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to update project.');
    } finally {
      setSavingId('');
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Delete this project from public/admin active list? You can restore it from Deleted Projects.')) return;
    await adminApi.delete(`/admin/projects/${projectId}`);
    await loadProjects();
  };

  const restoreProject = async (projectId) => {
    await adminApi.post(`/admin/projects/${projectId}/restore`);
    await loadProjects();
  };

  const permanentDeleteProject = async (projectId) => {
    if (!window.confirm('Permanently delete this project and its responses? This cannot be undone.')) return;
    await adminApi.delete(`/admin/projects/${projectId}/permanent`);
    await loadProjects();
  };

  const allVisibleProjectsSelected = projects.length > 0 && projects.every((project) => selectedProjectIds.includes(project.id));

  const toggleProjectSelection = (id, checked) => {
    setSelectedProjectIds((current) => (checked ? Array.from(new Set([...current, id])) : current.filter((item) => item !== id)));
  };

  const toggleAllVisibleProjects = () => {
    setSelectedProjectIds(allVisibleProjectsSelected ? [] : projects.map((project) => project.id).filter(Boolean));
  };

  const handleBulkProjectAction = async (action) => {
    if (selectedProjectIds.length === 0) return;
    const confirmText =
      action === 'permanent_delete'
        ? `Permanently delete ${selectedProjectIds.length} selected project(s) and responses? This cannot be undone.`
        : action === 'restore'
          ? `Undo delete for ${selectedProjectIds.length} selected project(s)?`
          : `Move ${selectedProjectIds.length} selected project(s) to Deleted Projects?`;
    if (!window.confirm(confirmText)) return;
    setBulkBusy(true);
    try {
      await adminApi.post('/admin/bulk/projects', { ids: selectedProjectIds, action });
      await loadProjects();
    } finally {
      setBulkBusy(false);
    }
  };

  const responsesForProject = (projectId) => responses.filter((response) => response.project_id === projectId);

  return (
    <div className="space-y-6">
      <section className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg sm:p-8">
        <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#FF6F00]">Project Approvals</p>
        <h2 className="mt-3 text-3xl font-bold text-gray-900">Review and approve project posts</h2>
        <p className="mt-3 max-w-3xl text-gray-600">
          Every project submitted by artists or collaborators lands here first. Publish approved posts, reject unclear posts, close completed posts, or deactivate anything unsafe.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <button type="button" onClick={() => setShowDeleted(false)} className={`${!showDeleted ? 'btn-primary' : 'btn-outline'}`}>
            Active Projects
          </button>
          <button type="button" onClick={() => setShowDeleted(true)} className={`${showDeleted ? 'btn-primary' : 'btn-outline'}`}>
            Deleted Projects
          </button>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3 rounded-2xl border border-orange-100 bg-orange-50/50 p-3">
          <button type="button" onClick={toggleAllVisibleProjects} className="btn-outline px-4 py-2 text-sm" disabled={projects.length === 0}>
            {allVisibleProjectsSelected ? 'Clear all visible' : 'Select all visible'}
          </button>
          {!showDeleted ? (
            <button
              type="button"
              onClick={() => handleBulkProjectAction('delete')}
              className="rounded-full border border-red-200 px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-50 disabled:opacity-50"
              disabled={bulkBusy || selectedProjectIds.length === 0}
            >
              Delete selected
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => handleBulkProjectAction('restore')}
                className="rounded-full border border-emerald-200 px-4 py-2 text-sm font-bold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                disabled={bulkBusy || selectedProjectIds.length === 0}
              >
                Undo selected
              </button>
              <button
                type="button"
                onClick={() => handleBulkProjectAction('permanent_delete')}
                className="rounded-full border border-red-200 px-4 py-2 text-sm font-bold text-red-700 hover:bg-red-50 disabled:opacity-50"
                disabled={bulkBusy || selectedProjectIds.length === 0}
              >
                Permanent delete selected
              </button>
            </>
          )}
          <span className="text-sm font-semibold text-gray-500">{selectedProjectIds.length} project(s) selected</span>
        </div>
        <div className="mt-6 grid gap-3 sm:grid-cols-4">
          {reviewOptions.map((item) => (
            <div key={item.value} className="rounded-2xl border border-orange-100 bg-orange-50/50 p-4">
              <p className="text-sm font-semibold text-gray-600">{item.label}</p>
              <p className="mt-2 text-3xl font-black text-gray-950">{counts[item.value] || 0}</p>
            </div>
          ))}
        </div>
      </section>

      {error && <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</p>}

      <section className="rounded-[2rem] border border-orange-100 bg-white shadow-lg">
        {loading ? (
          <div className="p-6 text-gray-500">Loading projects...</div>
        ) : projects.length === 0 ? (
          <div className="p-6 text-gray-500">No projects submitted yet.</div>
        ) : (
          <div className="divide-y divide-orange-100">
            {projects.map((project) => {
              const projectResponses = responsesForProject(project.id);
              return (
                <article key={project.id} className="grid gap-5 p-6 xl:grid-cols-[1fr_360px]">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedProjectIds.includes(project.id)}
                        onChange={(event) => toggleProjectSelection(project.id, event.target.checked)}
                        className="h-4 w-4 accent-orange-600"
                        aria-label="Select project"
                      />
                      <h3 className="text-xl font-bold text-gray-900">{project.title}</h3>
                      <span className={`rounded-full px-3 py-1 text-xs font-bold ${project.review_status === 'published' ? 'bg-green-100 text-green-700' : project.review_status === 'admin_rejected' ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-700'}`}>
                        {project.review_status || 'pending_review'}
                      </span>
                      {project.is_featured && <span className="rounded-full bg-purple-100 px-3 py-1 text-xs font-bold text-[#4A148C]">Featured</span>}
                      {!project.is_active && <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-bold text-slate-700">Inactive</span>}
                    </div>
                    <p className="mt-2 text-sm font-semibold text-gray-600">
                      {project.art_form} | {project.nature_of_work || 'Work details'} | {project.location || 'Location not added'} | {project.budget || 'Budget not added'}
                    </p>
                    <p className="mt-2 text-sm text-gray-500">Posted by {project.poster_name} ({project.poster_role})</p>
                    <p className="mt-3 max-w-4xl whitespace-pre-line text-sm leading-6 text-gray-700">{project.details}</p>
                    {project.image_url && <img src={project.image_url} alt={project.title} className="mt-4 h-40 w-full max-w-md rounded-2xl object-cover ring-1 ring-orange-100" />}
                    {projectResponses.length > 0 && (
                      <div className="mt-5 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                        <p className="text-sm font-black text-gray-900">Artist responses</p>
                        <div className="mt-3 space-y-2">
                          {projectResponses.map((response) => (
                            <div key={response.id || `${response.project_id}-${response.artist_id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white px-3 py-2">
                              <div>
                                <p className="text-sm font-bold text-gray-900">{response.artist_name}</p>
                                <p className="text-xs text-gray-500">{response.artist_email}</p>
                              </div>
                              <span className="rounded-full bg-purple-50 px-3 py-1 text-xs font-bold text-[#4A148C]">{statusLabel[response.status] || response.status}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3 rounded-2xl bg-orange-50/60 p-4">
                    <label className="block text-sm font-bold text-gray-800">
                      Review status
                      <select
                        value={project.review_status || 'pending_review'}
                        onChange={(event) => updateProject(project, { review_status: event.target.value })}
                        className="mt-1 w-full rounded-xl border border-orange-100 bg-white px-3 py-2"
                        disabled={savingId === project.id}
                      >
                        {reviewOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                      </select>
                    </label>
                    <label className="flex items-center gap-3 text-sm font-semibold text-gray-700">
                      <input type="checkbox" checked={Boolean(project.is_active)} onChange={(event) => updateProject(project, { is_active: event.target.checked })} disabled={savingId === project.id} />
                      Active
                    </label>
                    <label className="flex items-center gap-3 text-sm font-semibold text-gray-700">
                      <input type="checkbox" checked={Boolean(project.is_featured)} onChange={(event) => updateProject(project, { is_featured: event.target.checked })} disabled={savingId === project.id} />
                      Featured
                    </label>
                    <textarea
                      defaultValue={project.admin_note || ''}
                      onBlur={(event) => {
                        if (event.target.value !== (project.admin_note || '')) {
                          updateProject(project, { admin_note: event.target.value });
                        }
                      }}
                      placeholder="Admin note for rejection or internal review"
                      className="min-h-[90px] w-full rounded-xl border border-orange-100 px-3 py-2 text-sm"
                    />
                    {showDeleted ? (
                      <>
                        <button type="button" onClick={() => restoreProject(project.id)} className="w-full rounded-full border border-emerald-200 px-5 py-2 font-semibold text-emerald-700 hover:bg-emerald-50">
                          Undo Delete
                        </button>
                        <button type="button" onClick={() => permanentDeleteProject(project.id)} className="w-full rounded-full border border-red-200 px-5 py-2 font-semibold text-red-700 hover:bg-red-50">
                          Permanent Delete
                        </button>
                      </>
                    ) : (
                      <button type="button" onClick={() => deleteProject(project.id)} className="w-full rounded-full border border-red-200 px-5 py-2 font-semibold text-red-600 hover:bg-red-50">
                        Delete Project
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminProjects;
